/**
 * Global utility for managing shared webfleet properties.
 * @namespace Globals
 */
export const Globals = {
    /**
     * Initializes the webfleet object on the global window if it doesn't exist.
     * Makes the 'webfleet' property non-writable and non-configurable if it already exists.
     * @function init
     * @memberof Globals
     */
    init() {
        if (typeof window !== 'object') return
        if (Object.hasOwn(window, 'webfleet')) {
            const webfleetDescriptor = Object.getOwnPropertyDescriptor(window, 'webfleet')

            if (!webfleetDescriptor.configurable) {
                return
            }
            delete window.webfleet
        }

        Object.defineProperty(window, 'webfleet', {
            value: Object.create(null),
            writable: false,
            configurable: false
        })
    },

    /**
     * Sets a value for the specified key in the webfleet object.
     * If the key already exists and is configurable, it is deleted before setting the new value.
     * The property is made non-writable and non-configurable.
     * @function set
     * @memberof Globals
     * @param {string} key - The key to set the value for.
     * @param {*} value - The value to set.
     */
    set(key, value) {
        if (typeof window !== 'object') return
        this.init()
        if (Object.hasOwn(window.webfleet, key) && Object.getOwnPropertyDescriptor(window.webfleet, key).configurable) {
            delete window.webfleet[key]
        }
        if (!Object.hasOwn(window.webfleet, key) || Object.getOwnPropertyDescriptor(window.webfleet, key).writable) {
            Object.defineProperty(window.webfleet, key, { value, configurable: false, writable: false })
        }
    },

    /**
     * Sets a mutable value for the specified key in the webfleet object.
     * If the key already exists and is configurable, it is deleted before setting the new value.
     * The property is made writable but non-configurable.
     * @function setMutable
     * @memberof Globals
     * @param {string} key - The key to set the value for.
     * @param {*} value - The value to set.
     */
    setMutable(key, value) {
        if (typeof window !== 'object') return
        this.init()
        if (Object.hasOwn(window.webfleet, key) && Object.getOwnPropertyDescriptor(window.webfleet, key).configurable) {
            delete window.webfleet[key]
        }
        if (!Object.hasOwn(window.webfleet, key) || Object.getOwnPropertyDescriptor(window.webfleet, key).writable) {
            Object.defineProperty(window.webfleet, key, { value, configurable: false, writable: true })
        }
    },

    /**
     * Retrieves the value associated with the specified key from the webfleet object. Should only be used
     * at runtime and not during initialization, so the values can be set from any ui at any point in time.
     *
     * @function get
     * @memberof Globals
     * @param {string} key - The key to retrieve the value for.
     * @returns {*} The value associated with the specified key, or undefined if the key doesn't exist.
     */
    get(key, fallback) {
        if (typeof window?.webfleet?.[key] === 'undefined' && typeof fallback === 'undefined') {
            console.warn(
                `Trying to access global ${key} which has not been set.` +
                    ` Use Globals.set('${key}', 'someValue') from utilities to initialize.`
            )
        }
        return window?.webfleet?.[key] ?? fallback
    }
}
